import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { animateModalGlide } from '@/ui/modalGlide';
import { lagRundetRekt } from '@/ui/RoundedRect';
import { foretrekkerRedusertBevegelse } from '@/systems/motionPreferanser';
import { sfx } from '@/systems/sfx';

const SHEET_WIDTH = 860;
const GULL = 0xb8860b;
const OKER = 0xd9a14b;

const SJANGER_LABEL: Record<string, string> = {
  KRIM: 'Krim',
  ROMANCE: 'Romanse',
  LITERARY: 'Skjønnlitteratur',
  BARNEBOK: 'Barnebok',
  POETRY: 'Lyrikk',
  NONFICTION: 'Sakprosa',
  FANTASY: 'Fantasy',
  UNGDOM: 'Ungdomsbok',
  FEELGOOD: 'Feelgood',
};

export interface LanseringData {
  tittel: string;
  forfatter: string;
  tier: string;
  genre: string;
}

/**
 * LanseringModal — utgivelsesøyeblikket som en seremoni i stedet for et
 * stille statusbytte. Boka «avdukes» med konfetti, og en linje peker fram
 * mot anmelderdagen — det bygger forventningen som dom-dagen så innfrir.
 */
export class LanseringModal {
  private layer?: Phaser.GameObjects.Container;
  private onClose?: () => void;

  constructor(private scene: Phaser.Scene) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  open(data: LanseringData, onClose?: () => void): void {
    this.close();
    this.onClose = onClose;
    const redusert = foretrekkerRedusertBevegelse();

    this.layer = this.scene.add.container(0, 0).setDepth(205);
    animateModalGlide(this.scene, this.layer);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.8)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    const sheetH = 940;
    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = (GAME_HEIGHT - sheetH) / 2;
    const midt = GAME_WIDTH / 2;

    const sheet = lagRundetRekt(
      this.scene,
      sheetX,
      sheetY,
      SHEET_WIDTH,
      sheetH,
      COLORS.white,
      1,
      14,
    ).setStrokeStyle(4, COLORS.wine, 1);
    this.layer.add(sheet);
    this.layer.add(lagRundetRekt(this.scene, sheetX, sheetY, SHEET_WIDTH, 12, GULL, 1, 4));

    let y = sheetY + 40;
    this.layer.add(
      this.scene.add
        .text(midt, y, 'UTGIVELSESDAG', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#8b5e3c',
          fontStyle: '700',
        })
        .setOrigin(0.5, 0),
    );
    y += 44;

    // Faux-cover: en stilisert bok i vinrødt med tittel + forfatter.
    const coverW = 380;
    const coverH = 460;
    const coverX = midt - coverW / 2;
    const coverY = y;
    this.layer.add(
      lagRundetRekt(this.scene, coverX, coverY, coverW, coverH, COLORS.wine, 1, 10).setStrokeStyle(
        2,
        GULL,
        0.85,
      ),
    );
    // Rygg-stripe til venstre for bok-følelse.
    this.layer.add(
      this.scene.add.rectangle(coverX + 26, coverY, 3, coverH, OKER, 0.6).setOrigin(0, 0),
    );
    // Oker-regel over og under tittelen.
    this.layer.add(
      this.scene.add.rectangle(coverX + 56, coverY + 130, coverW - 112, 2, OKER, 0.9).setOrigin(0, 0),
    );

    this.layer.add(
      this.scene.add
        .text(midt + 13, coverY + 200, `«${data.tittel}»`, {
          fontFamily: FONTS.display,
          fontSize: fontStr(34),
          color: '#f6ecd8',
          align: 'center',
          wordWrap: { width: coverW - 110 },
        })
        .setOrigin(0.5),
    );
    this.layer.add(
      this.scene.add
        .text(midt + 13, coverY + coverH - 70, data.forfatter, {
          fontFamily: FONTS.body,
          fontSize: fontStr(20),
          color: '#e9d8a8',
          fontStyle: 'italic',
        })
        .setOrigin(0.5),
    );

    // Tier-badge oppe i høyre hjørne av coveret.
    const badgeR = 30;
    this.layer.add(
      this.scene.add.circle(coverX + coverW - 44, coverY + 44, badgeR, GULL, 1).setOrigin(0.5),
    );
    this.layer.add(
      this.scene.add
        .text(coverX + coverW - 44, coverY + 44, data.tier, {
          fontFamily: FONTS.display,
          fontSize: fontStr(26),
          color: '#3a2010',
          fontStyle: '700',
        })
        .setOrigin(0.5),
    );

    y = coverY + coverH + 36;
    this.layer.add(
      this.scene.add
        .text(midt, y, 'Boka er ute!', {
          fontFamily: FONTS.display,
          fontSize: fontStr(34),
          color: '#862736',
        })
        .setOrigin(0.5, 0),
    );
    y += 54;

    const sjanger = SJANGER_LABEL[data.genre] ?? data.genre;
    this.layer.add(
      this.scene.add
        .text(midt, y, `av ${data.forfatter}  ·  ${sjanger}  ·  ${data.tier}-tier`, {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#3a2e22',
        })
        .setOrigin(0.5, 0),
    );
    y += 40;

    this.layer.add(
      this.scene.add
        .text(midt, y, 'Bokhandlene har den nå. Kritikerne melder seg om noen dager.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(16),
          color: '#8b5e3c',
          fontStyle: 'italic',
          align: 'center',
          wordWrap: { width: SHEET_WIDTH - 120 },
        })
        .setOrigin(0.5, 0),
    );

    // Knapp
    const knappW = 260;
    const knappH = 62;
    const knappX = midt - knappW / 2;
    const knappY = sheetY + sheetH - knappH - 30;
    const knappBg = lagRundetRekt(this.scene, knappX, knappY, knappW, knappH, COLORS.wine, 1, 8);
    this.layer.add(knappBg);
    this.layer.add(
      this.scene.add
        .text(knappX + knappW / 2, knappY + knappH / 2, 'Skål  →', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(22),
          color: '#ffffff',
          fontStyle: '700',
        })
        .setOrigin(0.5),
    );
    knappBg.setInteractive({ useHandCursor: true });
    knappBg.on('pointerover', () => knappBg.setFillStyle(0x6f1f2c));
    knappBg.on('pointerout', () => knappBg.setFillStyle(COLORS.wine));
    knappBg.on('pointerup', () => this.close());

    sfx.play('cha-ching');
    if (!redusert) {
      this.scene.time.delayedCall(120, () => this.kjorKonfetti());
    }
  }

  close(): void {
    this.layer?.destroy(true);
    this.layer = undefined;
    const cb = this.onClose;
    this.onClose = undefined;
    cb?.();
  }

  private kjorKonfetti(): void {
    if (!this.layer) return;
    const farger = [COLORS.wine, GULL, OKER];
    const lag = this.scene.add.container(0, 0).setDepth(215);
    this.layer.add(lag);
    for (let i = 0; i < 30; i += 1) {
      const x = Math.random() * GAME_WIDTH;
      const farge = farger[Math.floor(Math.random() * farger.length)];
      const str = 6 + Math.floor(Math.random() * 8);
      const p = this.scene.add.rectangle(x, -20, str, str, farge, 1).setOrigin(0.5);
      p.setAngle(Math.random() * 360);
      lag.add(p);
      this.scene.tweens.add({
        targets: p,
        y: GAME_HEIGHT + 40,
        angle: p.angle + 360 * (Math.random() > 0.5 ? 1 : -1),
        alpha: 0,
        duration: 2600 + Math.random() * 1500,
        ease: 'Cubic.easeIn',
        delay: Math.random() * 500,
        onComplete: () => p.destroy(),
      });
    }
  }
}
